/**
 * @fileoverview User Types tab component for managing professional profiles.
 * Allows users to view their current types and add new professional profiles.
 */

import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Divider,
    Card,
    CardContent,
    Button,
    Chip,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Stepper,
    Step,
    StepLabel,
    IconButton,
    Tooltip
} from "@mui/material";
import {
    Add as AddIcon,
    MedicalServices as DoctorIcon,
    LocalPharmacy as PharmacistIcon,
    Business as InstitutionIcon,
    CheckCircle as ActiveIcon,
    HourglassEmpty as PendingIcon,
    Warning as WarningIcon,
    School as TrainingIcon,
    Description as DocumentIcon,
    Delete as DeleteIcon
} from "@mui/icons-material";
import {
    intl,
    fetchData,
    HTTP_STATUS,
    Notifications,
    DmsSelect,
    useDmsLogoffTabs
} from "dmsbasereact";
import Swal from "sweetalert2";
import { TextFieldCrm } from "../../../components/TextFieldCRM/TextFieldCRM";
import { TextFieldCrf } from "../../../components/TextFieldCRF/TextFieldCRF";
import { getBrazilianStateOptions } from "../../../constants/BrazilianStates";
import { InstitutionStatusEnum } from "../../../constants/InstitutionStatus";
import { DocumentsTab } from "./DocumentsTab";

const TYPE_CONFIG = {
    doctor: {
        label: "Médico",
        labelKey: "UserType.Doctor",
        icon: <DoctorIcon />,
        color: "primary",
        description: "Prescritor de medicamentos controlados",
        descriptionKey: "UserType.Doctor.Description",
        requiredFields: ["crm", "crm_state"],
        approvalSteps: [
            { key: "cfm", label: "Validação CRM" },
            { key: "approval", label: "Aprovação" },
            { key: "training", label: "Treinamento" }
        ]
    },
    pharmacist: {
        label: "Farmacêutico",
        labelKey: "UserType.Pharmacist",
        icon: <PharmacistIcon />,
        color: "secondary",
        description: "Profissional responsável pela dispensação",
        descriptionKey: "UserType.Pharmacist.Description",
        requiredFields: ["crf", "crf_state"],
        approvalSteps: [
            { key: "documents", label: "Documentos" },
            { key: "approval", label: "Aprovação" },
            { key: "training", label: "Treinamento" }
        ]
    },
    institution_responsible: {
        label: "Responsável Institucional",
        labelKey: "UserType.InstitutionResponsible",
        icon: <InstitutionIcon />,
        color: "info",
        description: "Responsável pela instituição de saúde",
        descriptionKey: "UserType.InstitutionResponsible.Description",
        requiredFields: [],
        approvalSteps: [
            { key: "documents", label: "Documentos" },
            { key: "approval", label: "Aprovação" },
            { key: "training", label: "Treinamento" }
        ]
    },
    distributor_responsible: {
        label: "Responsável Distribuidora",
        labelKey: "UserType.DistributorResponsible",
        icon: <InstitutionIcon />,
        color: "success",
        description: "Responsável pela gestão de distribuidoras",
        descriptionKey: "UserType.DistributorResponsible.Description",
        requiredFields: [],
        approvalSteps: [
            { key: "documents", label: "Documentos" },
            { key: "approval", label: "Aprovação" },
            { key: "training", label: "Treinamento" }
        ]
    }
};

const ACCOUNT_STATUS = {
    PENDING_CFM_VALIDATION: 1,
    PENDING_DOCUMENT_UPLOAD: 2,
    PENDING_APPROVAL: 3,
    PENDING_TRAINING: 4,
    ACTIVE: 5,
    REJECTED: 6,
    SUSPENDED: 7,
    PENDING_APPROVAL_GESTAO_LENA: 8,
    PENDING_APPROVAL_REGULATORIO: 9,
};

const STATUS_CONFIG = {
    1: { label: "Aguardando Validação CFM", labelKey: "AccountStatus.PendingCFMValidation", color: "warning", icon: <PendingIcon />, actionable: true },
    2: { label: "Aguardando Documentos", labelKey: "AccountStatus.PendingDocuments", color: "warning", icon: <DocumentIcon />, actionable: true },
    5: { label: "Ativo", labelKey: "Active", color: "success", icon: <ActiveIcon />, actionable: false },
    6: { label: "Rejeitado", labelKey: "Rejected", color: "error", icon: <WarningIcon />, actionable: false },
    8: { label: "Aguardando Aprovação", labelKey: "AccountStatus.PendingApproval", color: "info", icon: <PendingIcon />, actionable: false },
    9: { label: "Aguardando Regulatório", labelKey: "AccountStatus.PendingRegulatorio", color: "info", icon: <PendingIcon />, actionable: false },
    4: { label: "Aguardando Treinamento", labelKey: "AccountStatus.PendingTraining", color: "warning", icon: <TrainingIcon />, actionable: true },
};

const getStepIndex = (status, isDoctor) => {
    if (isDoctor) {
        if (status === ACCOUNT_STATUS.PENDING_CFM_VALIDATION) return 0;
        if (status === ACCOUNT_STATUS.PENDING_DOCUMENT_UPLOAD) return 1;
        if (status === ACCOUNT_STATUS.PENDING_APPROVAL_GESTAO_LENA) return 2;
        if (status === ACCOUNT_STATUS.PENDING_APPROVAL_REGULATORIO) return 2;
        if (status === ACCOUNT_STATUS.PENDING_TRAINING) return 3;
        if (status === ACCOUNT_STATUS.ACTIVE) return 4;
    } else {
        if (status === ACCOUNT_STATUS.PENDING_DOCUMENT_UPLOAD) return 0;
        if (status === ACCOUNT_STATUS.PENDING_APPROVAL_GESTAO_LENA) return 1;
        if (status === ACCOUNT_STATUS.PENDING_APPROVAL_REGULATORIO) return 1;
        if (status === ACCOUNT_STATUS.PENDING_TRAINING) return 2;
        if (status === ACCOUNT_STATUS.ACTIVE) return 3;
    }
    return 0;
};

/**
 * Get step status label (Approved, Pending, Rejected)
 * @param {number} stepIndex - Current step index
 * @param {number} activeStep - Active step in the stepper
 * @param {number} status - Account status
 * @returns {Object|null} Label info with text and color
 */
const getStepStatusLabel = (stepIndex, activeStep, status) => {
    const isCompleted = stepIndex < activeStep;
    const isActive = stepIndex === activeStep;
    const isRejected = status === ACCOUNT_STATUS.REJECTED;

    if (isCompleted) {
        return {
            text: intl.get("Approved").d("Approved"),
            color: "success.main"
        };
    }
    if (isActive && isRejected) {
        return {
            text: intl.get("Rejected").d("Rejected"),
            color: "error.main"
        };
    }
    if (isActive) {
        return {
            text: intl.get("Pending").d("Pending"),
            color: "primary.main"
        };
    }
    return null;
};

export const UserTypesTab = ({ onTypeAdded }) => {
    const [loading, setLoading] = useState(true);
    const [typesData, setTypesData] = useState(null);
    const [error, setError] = useState(null);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [selectedType, setSelectedType] = useState(null);
    const [formData, setFormData] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [availableInstitutions, setAvailableInstitutions] = useState([]);
    const [loadingInstitutions, setLoadingInstitutions] = useState(false);

    const stateOptions = getBrazilianStateOptions().map(({ value, label }) => ({
        label,
        value,
    }));

    const fetchTypes = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchData("/person/my_types", "GET", true);
            if (response.status === HTTP_STATUS.OK_200) {
                const data = response.data?.data || response.data || {};
                setTypesData(data);
            } else {
                setError(response.data?.detail || intl.get("ErrorLoadingTypes").d("Erro ao carregar tipos"));
            }
        } catch (err) {
            setError(intl.get("ErrorLoadingTypes").d("Erro ao carregar tipos"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTypes();
    }, [fetchTypes]);

    const handleOpenAddDialog = async (typeName) => {
        setSelectedType(typeName);
        setFormData({});
        setAddDialogOpen(true);

        // If adding institution_responsible, fetch available institutions
        if (typeName === "institution_responsible") {
            setLoadingInstitutions(true);
            try {
                const response = await fetchData("/person/my_institutions", "GET", true);
                if (response.status === HTTP_STATUS.OK_200) {
                    const institutions = response.data?.data || response.data || [];
                    const activeInstitutions = institutions.filter(
                        inst => inst.institution_status === InstitutionStatusEnum.APPROVED
                    );
                    setAvailableInstitutions(activeInstitutions);
                } else {
                    Notifications.toast(
                        intl.get("ErrorLoadingInstitutions").d("Error loading institutions"),
                        Notifications.TYPE_ERROR
                    );
                    setAvailableInstitutions([]);
                }
            } catch (err) {
                Notifications.toast(
                    intl.get("ErrorLoadingInstitutions").d("Error loading institutions"),
                    Notifications.TYPE_ERROR
                );
                setAvailableInstitutions([]);
            } finally {
                setLoadingInstitutions(false);
            }
        }
    };

    const handleCloseAddDialog = () => {
        setAddDialogOpen(false);
        setSelectedType(null);
        setFormData({});
        setAvailableInstitutions([]);
    };

    const handleDeleteType = async (type) => {
        const typeConfig = TYPE_CONFIG[type.type];
        if (!typeConfig) return;

        // Show confirmation dialog
        const result = await Swal.fire({
            icon: "warning",
            title: intl.get("RemoveProfile.Confirm.Title").d("Remove Professional Profile"),
            html: `
                <div style="text-align: left;">
                    <p><strong>${intl.get("RemoveProfile.Confirm.TypeLabel").d("Profile")}:</strong> ${intl.get(typeConfig.labelKey).d(typeConfig.label)}</p>
                    <hr/>
                    <p>${intl.get("RemoveProfile.Confirm.Warning").d("Are you sure you want to remove this professional profile?")}</p>
                    <p><strong>${intl.get("RemoveProfile.Confirm.Note").d("Important")}:</strong></p>
                    <ul>
                        <li>${intl.get("RemoveProfile.Confirm.LoseAccess").d("You will lose access to features associated with this profile")}</li>
                        <li>${intl.get("RemoveProfile.Confirm.CanAddLater").d("You can add this profile back later if needed")}</li>
                        <li>${intl.get("RemoveProfile.Confirm.OtherProfiles").d("Your other profiles will not be affected")}</li>
                    </ul>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: intl.get("Remove").d("Remove"),
            cancelButtonText: intl.get("Cancel").d("Cancel"),
            confirmButtonColor: "#d32f2f",
            cancelButtonColor: "#1976d2"
        });

        if (!result.isConfirmed) return;

        try {
            const response = await fetchData(`/person/remove_type/${type.type}`, "DELETE", true);

            if (response.status === HTTP_STATUS.OK_200) {
                Notifications.toast(
                    intl.get("RemoveProfile.Success").d("Profile removed successfully!"),
                    Notifications.TYPE_SUCCESS
                );
                await fetchTypes();
                if (onTypeAdded) onTypeAdded(); // Refresh parent component if needed
            } else {
                Notifications.toast(
                    response.data?.detail || intl.get("RemoveProfile.Error").d("Error removing profile"),
                    Notifications.TYPE_ERROR
                );
            }
        } catch (err) {
            Notifications.toast(
                err.response?.data?.detail || intl.get("RemoveProfile.Error").d("Error removing profile"),
                Notifications.TYPE_ERROR
            );
        }
    };

    const handleConfirmAdd = async () => {
        const typeConfig = TYPE_CONFIG[selectedType];
        if (!typeConfig) return;

        // Validate required fields
        for (const field of typeConfig.requiredFields) {
            if (!formData[field]) {
                Notifications.toast(
                    intl.get("RequiredFieldMissing").d("Fill in all required fields"),
                    Notifications.TYPE_ERROR
                );
                return;
            }
        }

        // Show confirmation with detailed explanation
        const result = await Swal.fire({
            icon: "warning",
            title: intl.get("AddProfile.Confirm.Title").d("Add New Profile"),
            html: `
                <div style="text-align: left;">
                    <p><strong>${intl.get("AddProfile.Confirm.TypeLabel").d("Type")}:</strong> ${intl.get(typeConfig.labelKey).d(typeConfig.label)}</p>
                    <hr/>
                    <p>${intl.get("AddProfile.Confirm.Warning").d("By adding this profile, you will go through a new approval process:")}</p>
                    <ul>
                        ${typeConfig.approvalSteps.map(step => `<li>${intl.get(`ApprovalStep.${step.key}`).d(step.label)}</li>`).join('')}
                    </ul>
                    <p><strong>${intl.get("AddProfile.Confirm.Note").d("Important")}:</strong> ${intl.get("AddProfile.Confirm.NoteText").d("Your already approved profiles will continue to work normally during this process.")}</p>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: intl.get("Confirm").d("Confirm"),
            cancelButtonText: intl.get("Cancel").d("Cancel"),
            confirmButtonColor: "#1976d2",
        });

        if (!result.isConfirmed) return;

        setSubmitting(true);
        try {
            const response = await fetchData(`/person/add_type/${selectedType}`, "POST", true, formData);

            if (response.status === HTTP_STATUS.CREATED_201 || response.status === HTTP_STATUS.OK_200) {
                Notifications.toast(
                    intl.get("AddProfile.Success").d("Profile added successfully! Start the approval process."),
                    Notifications.TYPE_SUCCESS
                );
                handleCloseAddDialog();
                await fetchTypes();
                if (onTypeAdded) onTypeAdded();
            } else {
                Notifications.toast(
                    response.data?.detail || intl.get("AddProfile.Error").d("Error adding profile"),
                    Notifications.TYPE_ERROR
                );
            }
        } catch (err) {
            Notifications.toast(
                intl.get("AddProfile.Error").d("Error adding profile"),
                Notifications.TYPE_ERROR
            );
        } finally {
            setSubmitting(false);
        }
    };

    const renderPendingTypeContent = (type, typeConfig) => {
        const isDoctor = type.type === "doctor";
        const status = type.status;

        // Determine what action is needed based on status
        const renderActionContent = () => {
            switch (status) {
                case ACCOUNT_STATUS.PENDING_CFM_VALIDATION:
                    return (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                {intl.get("TypeRequest.PendingCfm").d("Your CRM is being validated with CFM. This process may take a few minutes.")}
                            </Typography>
                        </Alert>
                    );
                case ACCOUNT_STATUS.PENDING_DOCUMENT_UPLOAD:
                    return (
                        <Box sx={{ mt: 2 }}>
                            {type.status_reason && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                                        {intl.get("TypeRequest.RejectionReason").d("Rejection reason:")}
                                    </Typography>
                                    <Typography variant="body2">
                                        {type.status_reason}
                                    </Typography>
                                </Alert>
                            )}
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    {type.status_reason
                                        ? intl.get("TypeRequest.PendingDocumentsAfterRejection").d("Make the necessary adjustments and resubmit the documents for approval.")
                                        : intl.get("TypeRequest.PendingDocuments").d("Upload the required documents to continue the approval process.")
                                    }
                                </Typography>
                            </Alert>
                            <DocumentsTab onStatusChange={fetchTypes} />
                        </Box>
                    );
                case ACCOUNT_STATUS.PENDING_APPROVAL_GESTAO_LENA:
                case ACCOUNT_STATUS.PENDING_APPROVAL_REGULATORIO:
                    return (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                {intl.get("TypeRequest.PendingApproval").d("Your documents are being reviewed. You will be notified when the approval is completed.")}
                            </Typography>
                        </Alert>
                    );
                case ACCOUNT_STATUS.PENDING_TRAINING:
                    return (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            <Typography variant="body2" gutterBottom>
                                {intl.get("TypeRequest.PendingTraining").d("Your account has been approved! Complete the training to activate this profile.")}
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<TrainingIcon />}
                                onClick={() => window.location.href = "/training-video"}
                                sx={{ mt: 1 }}
                            >
                                {intl.get("GoToTraining").d("Go to Training")}
                            </Button>
                        </Alert>
                    );
                default:
                    return null;
            }
        };

        const activeStep = getStepIndex(status, isDoctor);

        return (
            <Box>
                <Stepper
                    activeStep={activeStep}
                    alternativeLabel
                    sx={{ mt: 2, mb: 2 }}
                >
                    {typeConfig.approvalSteps.map((step, index) => {
                        const stepStatus = getStepStatusLabel(index, activeStep, status);
                        return (
                            <Step key={step.key} completed={activeStep > index}>
                                <StepLabel
                                    optional={
                                        stepStatus ? (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: stepStatus.color,
                                                    fontWeight: 500
                                                }}
                                            >
                                                {stepStatus.text}
                                            </Typography>
                                        ) : null
                                    }
                                >
                                    {intl.get(`ApprovalStep.${step.key}`).d(step.label)}
                                </StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
                {renderActionContent()}
            </Box>
        );
    };

    /**
     * Check if a specific type can be deleted
     * RULES:
     * - Doctor: NEVER can be deleted
     * - Pharmacist: NEVER can be deleted
     * - Distributor Responsible: NEVER can be deleted
     * - Institution Responsible: Can ONLY be deleted if user also has Pharmacist
     */
    const canDeleteType = (typeName, currentTypes) => {
        const typeNames = currentTypes.map(t => t.type);
        const hasPharmacist = typeNames.includes("pharmacist");

        // If there's any pending type (including PersonTypeRequest), don't allow deleting ANY profile
        // This prevents users from deleting profiles during the approval process
        const hasPendingType = currentTypes.some(t => t.status !== ACCOUNT_STATUS.ACTIVE);
        if (hasPendingType) {
            return false;
        }

        // These types can NEVER be deleted
        if (typeName === "doctor" || typeName === "pharmacist" || typeName === "distributor_responsible") {
            return false;
        }

        // Institution Responsible can ONLY be deleted if user also has Pharmacist
        if (typeName === "institution_responsible") {
            return hasPharmacist;
        }

        // Default: cannot delete if it's the only type
        return currentTypes.length > 1;
    };

    const renderTypeCard = (type) => {
        const typeConfig = TYPE_CONFIG[type.type];
        if (!typeConfig) return null;

        const statusConfig = STATUS_CONFIG[type.status] || { label: type.status_label, color: "default" };
        const isPending = type.status !== ACCOUNT_STATUS.ACTIVE;

        // Check if this specific type can be deleted
        const showDeleteButton = canDeleteType(type.type, currentTypes);

        return (
            <Card key={type.type} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        mb: isPending ? 0 : 2
                    }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                            {typeConfig.icon}
                            <Typography variant="h6">
                                {intl.get(typeConfig.labelKey).d(typeConfig.label)}
                            </Typography>
                        </Box>
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexShrink: 0
                        }}>
                            <Chip
                                icon={statusConfig.icon}
                                label={type.status_label || intl.get(statusConfig.labelKey).d(statusConfig.label)}
                                color={statusConfig.color}
                                size="small"
                            />
                            {showDeleteButton && (
                                <Tooltip title={intl.get("RemoveProfile").d("Remove Profile")}>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDeleteType(type)}
                                        aria-label="remove profile"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                    {!isPending && (
                        <Typography variant="body2" color="text.secondary">
                            {intl.get(typeConfig.descriptionKey).d(typeConfig.description)}
                        </Typography>
                    )}
                    {isPending && renderPendingTypeContent(type, typeConfig)}
                </CardContent>
            </Card>
        );
    };

    const renderAddTypeCard = (typeName) => {
        const typeConfig = TYPE_CONFIG[typeName];
        if (!typeConfig) return null;

        return (
            <Card
                key={typeName}
                variant="outlined"
                sx={{
                    mb: 2,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                        borderColor: "primary.main",
                        boxShadow: 2
                    }
                }}
                onClick={() => handleOpenAddDialog(typeName)}
            >
                <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {typeConfig.icon}
                            <Box>
                                <Typography variant="h6">
                                    {intl.get(typeConfig.labelKey).d(typeConfig.label)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {intl.get(typeConfig.descriptionKey).d(typeConfig.description)}
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            size="small"
                        >
                            {intl.get("Add").d("Add")}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        );
    };

    const renderAddDialog = () => {
        const typeConfig = selectedType ? TYPE_CONFIG[selectedType] : null;
        if (!typeConfig) return null;

        return (
            <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {typeConfig.icon}
                    {intl.get("AddProfile.Dialog.Title").d("Add Profile")}: {intl.get(typeConfig.labelKey).d(typeConfig.label)}
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        {intl.get("AddProfile.Dialog.Info").d("By adding this profile, you will go through an approval process. Your already approved profiles will continue to work normally.")}
                    </Alert>

                    <Typography variant="subtitle2" gutterBottom>
                        {intl.get("AddProfile.Dialog.ApprovalSteps").d("Approval steps:")}
                    </Typography>
                    <Stepper alternativeLabel sx={{ mb: 3 }}>
                        {typeConfig.approvalSteps.map((step) => (
                            <Step key={step.key}>
                                <StepLabel>{intl.get(`ApprovalStep.${step.key}`).d(step.label)}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    <Divider sx={{ my: 2 }} />

                    {selectedType === "doctor" && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextFieldCrm
                                    fullWidth
                                    value={formData.crm || ""}
                                    onChange={(e) => setFormData({ ...formData, crm: e.target.value })}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DmsSelect
                                    options={stateOptions}
                                    label={intl.get("CRMState").d("CRM State")}
                                    name="crm_state"
                                    value={formData.crm_state || ""}
                                    onChange={(value) => setFormData({ ...formData, crm_state: value })}
                                    fullWidth
                                    required
                                />
                            </Grid>
                        </Grid>
                    )}

                    {selectedType === "pharmacist" && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextFieldCrf
                                    fullWidth
                                    value={formData.crf || ""}
                                    onChange={(e) => setFormData({ ...formData, crf: e.target.value })}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DmsSelect
                                    options={stateOptions}
                                    label={intl.get("CRFState").d("CRF State")}
                                    name="crf_state"
                                    value={formData.crf_state || ""}
                                    onChange={(value) => setFormData({ ...formData, crf_state: value })}
                                    fullWidth
                                    required
                                />
                            </Grid>
                        </Grid>
                    )}

                    {selectedType === "institution_responsible" && (
                        <Grid container spacing={2}>
                            {loadingInstitutions ? (
                                <Grid item xs={12}>
                                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                </Grid>
                            ) : (
                                <>
                                    <Grid item xs={12}>
                                        <DmsSelect
                                            options={availableInstitutions.map(inst => ({
                                                value: inst.pk,
                                                label: inst.name
                                            }))}
                                            label={intl.get("LinkedInstitutions").d("Instituições (vinculado a)")}
                                            name="linked_institutions"
                                            value={formData.linked_institutions || []}
                                            onChange={(value) => setFormData({ ...formData, linked_institutions: value })}
                                            fullWidth
                                            multiple
                                            helperText={intl.get("LinkedInstitutions.Helper").d("Selecione todas as instituições onde você trabalha")}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DmsSelect
                                            options={(formData.linked_institutions || []).map(pk => {
                                                const inst = availableInstitutions.find(i => i.pk === pk);
                                                return inst ? { value: inst.pk, label: inst.name } : null;
                                            }).filter(Boolean)}
                                            label={intl.get("ResponsibleForInstitutions").d("Responsável Por (como Usuário Institucional)")}
                                            name="responsible_for_institutions"
                                            value={formData.responsible_for_institutions || []}
                                            onChange={(value) => setFormData({ ...formData, responsible_for_institutions: value })}
                                            fullWidth
                                            multiple
                                            helperText={intl.get("ResponsibleForInstitutions.Helper").d("Quais instituições você é responsável? (Deve ser da lista acima)")}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddDialog} disabled={submitting}>
                        {intl.get("Cancel").d("Cancel")}
                    </Button>
                    <Button
                        onClick={handleConfirmAdd}
                        variant="contained"
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={20} /> : <AddIcon />}
                    >
                        {submitting ? intl.get("Adding").d("Adding...") : intl.get("AddProfile").d("Add Profile")}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error">{error}</Alert>
        );
    }

    const currentTypes = typesData?.types || [];
    const rawAvailableToAdd = typesData?.available_to_add || [];
    const userRoles = typesData?.user_roles || [];

    // Filter available types based on user's institutional roles
    const filterAvailableTypes = (currentTypes, availableTypes, roles) => {
        const currentTypeNames = currentTypes.map(t => t.type);

        // Check user's institutional roles (from institutional groups)
        const isDoctor = roles.includes("DOCTOR");
        const isPharmacist = roles.includes("PHARMACIST");
        const isDistributor = roles.includes("DISTRIBUTOR_RESPONSIBLE");
        const isInstitutionResponsible = roles.includes("INSTITUTION_RESPONSIBLE");

        // RULE 1: DOCTOR - Can only add/re-add doctor type
        if (isDoctor) {
            return availableTypes.filter(type => type === "doctor");
        }

        // RULE 2: DISTRIBUTOR_RESPONSIBLE - Can only add/re-add distributor_responsible type
        if (isDistributor) {
            return availableTypes.filter(type => type === "distributor_responsible");
        }

        // RULE 3: PHARMACIST - Can add pharmacist and institution_responsible
        if (isPharmacist) {
            return availableTypes.filter(type =>
                type === "pharmacist" || type === "institution_responsible"
            );
        }

        // RULE 4: INSTITUTION_RESPONSIBLE - Can add institution_responsible and pharmacist
        if (isInstitutionResponsible) {
            return availableTypes.filter(type =>
                type === "institution_responsible" || type === "pharmacist"
            );
        }

        // RULE 5: No specific role - show whatever backend sends (shouldn't happen normally)
        return availableTypes;
    };

    const availableToAdd = filterAvailableTypes(currentTypes, rawAvailableToAdd, userRoles);

    /**
     * Check if "Add New Profile" tab should be shown
     * RULES:
     * - Show if user has ONLY pharmacist (can add institution_responsible)
     * - Show if user has ONLY institution_responsible (can add pharmacist)
     * - DON'T show if user has both pharmacist AND institution_responsible
     * - DON'T show for doctor or distributor_responsible (they are standalone profiles)
     */
    const shouldShowAddTab = (currentTypes, availableToAdd) => {
        if (availableToAdd.length === 0) return false;

        const typeNames = currentTypes.map(t => t.type);
        const hasPharmacist = typeNames.includes("pharmacist");
        const hasInstitutionResponsible = typeNames.includes("institution_responsible");
        const hasDoctor = typeNames.includes("doctor");
        const hasDistributor = typeNames.includes("distributor_responsible");

        // Don't show for doctor or distributor (they can't add other types)
        if (hasDoctor || hasDistributor) {
            return false;
        }

        // Don't show if user already has both pharmacist and institution_responsible
        if (hasPharmacist && hasInstitutionResponsible) {
            return false;
        }

        // Show if user has only pharmacist OR only institution_responsible
        if (hasPharmacist || hasInstitutionResponsible) {
            return true;
        }

        // Default: don't show
        return false;
    };

    const showAddTab = shouldShowAddTab(currentTypes, availableToAdd);

    return (
        <Box>
            {/* Current Types */}
            <Typography variant="h6" gutterBottom>
                {intl.get("UserTypes.Current").d("Your Professional Profiles")}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {currentTypes.length > 0 ? (
                currentTypes.map(renderTypeCard)
            ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {intl.get("UserTypes.NoCurrent").d("You do not have any registered professional profiles yet.")}
                </Alert>
            )}

            {/* Available Types to Add */}
            {showAddTab && (
                <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                        {intl.get("UserTypes.Available").d("Add New Profile")}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {intl.get("UserTypes.AddWarning").d("When adding a new professional profile, you will need to go through a specific approval process for that profile. Your already approved profiles will not be affected.")}
                    </Alert>

                    {availableToAdd.map(renderAddTypeCard)}
                </>
            )}

            {renderAddDialog()}
        </Box>
    );
};
